"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const joi_1 = __importDefault(require("joi"));
const errors_1 = require("../../errors");
const ChargeStatus_1 = require("../models/charges/ChargeStatus");
const ChargeTypes_1 = require("../models/charges/ChargeTypes");
const rates_1 = require("../models/rates");
const calculateChargeAmount_1 = __importDefault(require("../utils/calculateChargeAmount"));
class ChargeValidator {
    transactionTypes = Object.values(rates_1.TransactionType);
    chargeStatus = Object.values(ChargeStatus_1.ChargeStatus);
    chargeTypes = Object.values(ChargeTypes_1.ChargeTypes);
    _adjustmentRecordSchema = joi_1.default.object({
        timestamp: joi_1.default.number().required(),
        email: joi_1.default.string().required(),
        change: joi_1.default.string().required(),
        note: joi_1.default.string().required(),
    });
    _chargeDetailSchema = joi_1.default.object({
        inboundReferenceNumber: joi_1.default.string().allow('').optional(),
        outboundReferenceNumber: joi_1.default.string().allow('').optional(),
        trackingNumber: joi_1.default.string().allow('').optional(),
        sku: joi_1.default.string().allow('').optional(),
        weight: joi_1.default.number().optional(),
        bolNumber: joi_1.default.string().allow('').optional(),
        poNumber: joi_1.default.string().allow('').optional(),
        lotNumber: joi_1.default.string().allow('').optional(),
        serialNumber: joi_1.default.string().allow('').optional(),
        muLabel: joi_1.default.string().allow('').optional(),
        storageUoM: joi_1.default.string().allow('').optional(),
        inventoryUoM: joi_1.default.string().allow('').optional(),
        trailerNumber: joi_1.default.string().allow('').optional(),
        locationName: joi_1.default.string().allow('').optional(),
        itemClassification: joi_1.default.string().allow('').optional(),
    });
    _chargeSchema = joi_1.default.object({
        id: joi_1.default.string().allow('').required(),
        orgKey: joi_1.default.string().required(),
        customerId: joi_1.default.string().required(),
        customerName: joi_1.default.string().required(),
        rateId: joi_1.default.string().allow('').optional(),
        eventId: joi_1.default.string().allow('').optional(),
        invoiceId: joi_1.default.string().allow('').optional(),
        invoiceNumber: joi_1.default.when('invoiceId', {
            is: joi_1.default.string().invalid('').exist(),
            then: joi_1.default.string().required(),
            otherwise: joi_1.default.string().valid('').optional(),
        }),
        warehouseId: joi_1.default.number().optional(),
        warehouseName: joi_1.default.string().allow('').optional(),
        class: joi_1.default.string().allow('').optional(),
        transactionId: joi_1.default.number().optional(),
        glAccount: joi_1.default.string().allow('').optional(),
        transactionType: joi_1.default.string()
            .valid(...this.transactionTypes)
            .required(),
        category: joi_1.default.string().required(),
        chargeType: joi_1.default.string()
            .valid(...this.chargeTypes)
            .required(),
        chargeLabel: joi_1.default.string().allow('').optional(),
        memo: joi_1.default.string().allow('').optional(),
        countingUnit: joi_1.default.string().allow('').optional(),
        countingMethod: joi_1.default.string().allow('').optional(),
        qty: joi_1.default.number().required(),
        chargePerUnit: joi_1.default.number().required(),
        total: joi_1.default.number().required(),
        status: joi_1.default.string()
            .valid(...this.chargeStatus)
            .required(),
        created: joi_1.default.number().required(),
        confirmDate: joi_1.default.number().optional(),
        transactionCreated: joi_1.default.number().optional(),
        chargeDetail: this._chargeDetailSchema.required(),
        chargeAdjustments: joi_1.default.array()
            .items(this._adjustmentRecordSchema)
            .required(),
        chargeEdits: joi_1.default.array().items(this._adjustmentRecordSchema).required(),
        isPending: joi_1.default.boolean().required(),
        predefinedManualRateId: joi_1.default.string().allow('').optional(),
        noMarkedUpChargePerUnit: joi_1.default.number().optional(),
    }).required();
    validate(charge) {
        const issues = [];
        const result = this._chargeSchema.validate(charge, {
            abortEarly: false,
            convert: false,
        });
        if (result.error) {
            result.error.details.forEach((issue) => {
                switch (issue.type) {
                    case 'any.required': {
                        issues.push(new errors_1.Errors.ValidationIssue('ICharge', errors_1.Errors.ValidationErrorCode.requiredValue, issue.context?.key, undefined));
                        break;
                    }
                    case 'object.unknown': {
                        issues.push(new errors_1.Errors.ValidationIssue('ICharge', errors_1.Errors.ValidationErrorCode.notAllowed, issue.context?.label ||
                            issue.context?.key, issue.context?.value));
                        break;
                    }
                    case 'object.base': {
                        issues.push(new errors_1.Errors.ValidationIssue('ICharge', errors_1.Errors.ValidationErrorCode.invalidType, issue.context?.label ||
                            issue.context?.key, issue.context?.value));
                        break;
                    }
                    case 'string.base':
                    case 'array.base':
                    case 'number.base': {
                        issues.push(new errors_1.Errors.ValidationIssue('ICharge', errors_1.Errors.ValidationErrorCode.invalidType, issue.context?.label ||
                            issue.context?.key, issue.context?.value));
                        break;
                    }
                    case 'any.only':
                    case 'string.empty': {
                        issues.push(new errors_1.Errors.ValidationIssue('ICharge', errors_1.Errors.ValidationErrorCode.invalidValue, issue.context?.label ||
                            issue.context?.key, issue.context?.value));
                        break;
                    }
                }
            });
        }
        if (charge?.qty &&
            charge?.total &&
            (0, calculateChargeAmount_1.default)(charge.qty, charge.chargePerUnit) !==
                charge?.total) {
            issues.push(new errors_1.Errors.ValidationIssue('ICharge', errors_1.Errors.ValidationErrorCode.invalidValue, 'total', charge?.total));
        }
        if (issues.length > 0) {
            const error = new errors_1.Errors.ValidationError();
            error.addValidationIssueList(issues);
            throw error;
        }
    }
}
exports.default = ChargeValidator;
